import { useEffect, useState, useCallback } from 'react';
import { useStore } from 'hooks/useStore';
import {
  bidAnalysisServices,
  AttributeWithUsers,
} from 'services/bidAnalysis/bidAnalysisServices';
import { authServices } from 'services/authServices';
import { toast } from 'react-toastify';
import {
  ManagerDashboardBid,
  ManagerDashboardResponse,
  FilterState,
  ViewMode,
  TabType,
  mapTemplateNameToDisplay,
} from './types';

const useManagerReviewLogic = () => {
  const {
    UserStore: { userMetadata },
  } = useStore();

  // State management
  const [bids, setBids] = useState<ManagerDashboardBid[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('review-queue');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  
  // Filter states - separate selected vs applied
  const [selectedFilters, setSelectedFilters] = useState<FilterState>({
    search: '',
    status: [],
    analysts: [],
    productTypes: [],
  });

  const [appliedFilters, setAppliedFilters] = useState<FilterState>({
    search: '',
    status: [],
    analysts: [],
    productTypes: [],
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(6);

  // Unique lists for filters
  const [allAnalysts, setAllAnalysts] = useState<string[]>([]);
  const [isLoadingAnalysts, setIsLoadingAnalysts] = useState(true);
  const [allProductTypes] = useState<string[]>(['MAS Direct', 'MAS Indirect', 'NC (National Contract)']);

  // Approved bids state
  const [approvedBids, setApprovedBids] = useState<ManagerDashboardBid[]>([]);
  const [approvedBidsLoading, setApprovedBidsLoading] = useState(false);
  const [approvedBidsTotalRecords, setApprovedBidsTotalRecords] = useState(0);
  const [approvedBidsCurrentPage, setApprovedBidsCurrentPage] = useState(0);
  
  // Approved bids filter states
  const [selectedApprovedBidsFilters, setSelectedApprovedBidsFilters] = useState<FilterState>({
    search: '',
    status: [],
    analysts: [],
    productTypes: [],
    reviewers: [],
    createdBy: [],
  });

  const [appliedApprovedBidsFilters, setAppliedApprovedBidsFilters] = useState<FilterState>({
    search: '',
    status: [],
    analysts: [],
    productTypes: [],
    reviewers: [],
    createdBy: [],
  });

  // Unique lists for approved bids filters
  const [approvedBidsAllAnalysts, setApprovedBidsAllAnalysts] = useState<string[]>([]);
  const [isLoadingApprovedBidsAnalysts, setIsLoadingApprovedBidsAnalysts] = useState(true);
  const [approvedBidsAllCreatedBy, _setApprovedBidsAllCreatedBy] = useState<string[]>([]);

  // Notes modal
  const [selectedBidForNotes, setSelectedBidForNotes] = useState<ManagerDashboardBid | null>(null);
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);

  // User attributes
  const [userAttributes, setUserAttributes] = useState<string[]>([]);
  const [isLoadingAttributes, setIsLoadingAttributes] = useState(false);

  // Get user email for filtering
  const userEmail = userMetadata?.userInfo?.email || '';

  // Fetch bids from API
  const fetchBids = useCallback(async (page: number = 0) => {
    setLoading(true);
    try {
      const backendPage = page + 1; // Frontend 0-based, backend 1-based

      // Build template_name query param from applied filters
      let templateNameQuery: string | undefined = undefined;
      if (appliedFilters.productTypes.length > 0) {
        // Map display names to API format (keep capitalization as-is)
        const apiTemplateNames = appliedFilters.productTypes.map((type) => {
          if (type === 'MAS Direct') return 'MAS Direct';
          if (type === 'MAS Indirect') return 'MAS Indirect';
          if (type === 'NC (National Contract)') return 'NC';
          return type;
        });
        templateNameQuery = apiTemplateNames.join(',');
      }

      // Build analyst query param
      const analystQuery = appliedFilters.analysts.length > 0 ? appliedFilters.analysts.join(',') : undefined;

      // Build status query param
      const statusQuery = appliedFilters.status.length > 0 ? appliedFilters.status.join(',') : undefined;

      console.log('Fetching bids with params:', {
        page: backendPage,
        page_size: pageSize,
        search: appliedFilters.search.trim() || undefined,
        status: statusQuery,
        analyst: analystQuery,
        template_name: templateNameQuery,
        reviewer: userEmail || undefined,
      });

      const response: ManagerDashboardResponse = await bidAnalysisServices.getManagerDashboard({
        page: backendPage,
        page_size: pageSize,
        search: appliedFilters.search.trim() || undefined,
        status: statusQuery,
        analyst: analystQuery,
        template_name: templateNameQuery,
        reviewer: userEmail || undefined,
      });

      // No need to filter by reviewer_name - backend handles it
      let filteredBids = response.data;

      // Map template names to display format
      filteredBids = filteredBids.map((bid) => ({
        ...bid,
        template_name: mapTemplateNameToDisplay(bid.template_name),
      }));

      // Sort notes by created_at descending (latest first)
      filteredBids.forEach((bid) => {
        if (bid.notes && bid.notes.length > 0) {
          bid.notes.sort((a, b) => {
            const dateA = new Date(b.created_at).getTime();
            const dateB = new Date(a.created_at).getTime();
            return dateA - dateB;
          });
        }
      });

      setBids(filteredBids);
      // Use pagination total_count from API response, not the current page's data length
      setTotalRecords(response.pagination?.total_count || filteredBids.length);
    } catch (error) {
      console.error('Error fetching manager dashboard:', error);
      toast.error('Failed to load bids. Please try again.');
      setBids([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, pageSize, userEmail]);

  // Fetch approved bids from API
  const fetchApprovedBids = useCallback(async (page: number = 0) => {
    setApprovedBidsLoading(true);
    try {
      const backendPage = page + 1; // Frontend 0-based, backend 1-based

      // Build template_name query param from applied filters
      let templateNameQuery: string | undefined = undefined;
      if (appliedApprovedBidsFilters.productTypes.length > 0) {
        // Map display names to API format
        const apiTemplateNames = appliedApprovedBidsFilters.productTypes.map((type) => {
          if (type === 'MAS Direct') return 'MAS Direct';
          if (type === 'MAS Indirect') return 'MAS Indirect';
          if (type === 'NC (National Contract)') return 'NC';
          return type;
        });
        templateNameQuery = apiTemplateNames.join(',');
      }

      // Build analyst query param
      const analystQuery = appliedApprovedBidsFilters.analysts.length > 0 
        ? appliedApprovedBidsFilters.analysts.join(',') 
        : undefined;

      // Always filter by current user's email as reviewer (they only see bids they approved)
      const reviewerQuery = userEmail || undefined;

      console.log('Fetching approved bids with params:', {
        page: backendPage,
        page_size: pageSize,
        analyst: analystQuery,
        reviewer: reviewerQuery,
        template_name: templateNameQuery,
      });

      const response: ManagerDashboardResponse = await bidAnalysisServices.getApprovedBids({
        page: backendPage,
        page_size: pageSize,
        analyst: analystQuery,
        reviewer: reviewerQuery,
        template_name: templateNameQuery,
      });

      let filteredBids = response.data;

      // Map template names to display format
      filteredBids = filteredBids.map((bid) => ({
        ...bid,
        template_name: mapTemplateNameToDisplay(bid.template_name),
      }));

      // Sort notes by created_at descending (latest first)
      filteredBids.forEach((bid) => {
        if (bid.notes && bid.notes.length > 0) {
          bid.notes.sort((a, b) => {
            const dateA = new Date(b.created_at).getTime();
            const dateB = new Date(a.created_at).getTime();
            return dateA - dateB;
          });
        }
      });

      setApprovedBids(filteredBids);
      setApprovedBidsTotalRecords(response.pagination?.total_count || filteredBids.length);
    } catch (error) {
      console.error('Error fetching approved bids:', error);
      toast.error('Failed to load approved bids. Please try again.');
      setApprovedBids([]);
      setApprovedBidsTotalRecords(0);
    } finally {
      setApprovedBidsLoading(false);
    }
  }, [appliedApprovedBidsFilters, pageSize, userEmail]);

  // Fetch bids when filters or page changes (for review queue)
  useEffect(() => {
    if (activeTab === 'review-queue' && userEmail) {
      fetchBids(currentPage);
    } else if (activeTab === 'review-queue') {
      // Keep loading state true until userEmail is available
      setLoading(true);
    }
  }, [fetchBids, currentPage, userEmail, activeTab]);

  // Fetch approved bids when tab is history and filters/page changes
  useEffect(() => {
    if (activeTab === 'history' && userEmail) {
      fetchApprovedBids(approvedBidsCurrentPage);
    } else if (activeTab === 'history') {
      // Keep loading state true until userEmail is available
      setApprovedBidsLoading(true);
    }
  }, [fetchApprovedBids, approvedBidsCurrentPage, activeTab, userEmail]);

  // Fetch users list for analyst filter (Review Queue)
  useEffect(() => {
    const fetchUsersList = async () => {
      setIsLoadingAnalysts(true);
      try {
        const response = await authServices.getUsersList();
        // Extract unique analyst names from users list
        const uniqueAnalysts = Array.from(
          new Set(response.users?.map((user) => user.name).filter((name): name is string => !!name) || [])
        ).sort();
        setAllAnalysts(uniqueAnalysts);
      } catch (error) {
        console.error('Error fetching users list:', error);
        // Fallback: keep empty array
        setAllAnalysts([]);
      } finally {
        setIsLoadingAnalysts(false);
      }
    };

    fetchUsersList();
  }, []);

  // Fetch users list for approved bids filters (Analyst only - reviewer is auto-filtered by userEmail)
  useEffect(() => {
    const fetchApprovedBidsUsersList = async () => {
      setIsLoadingApprovedBidsAnalysts(true);
      try {
        const response = await authServices.getUsersList();
        // Extract unique analyst names from users list
        const uniqueAnalysts = Array.from(
          new Set(response.users?.map((user) => user.name).filter((name): name is string => !!name) || [])
        ).sort();
        setApprovedBidsAllAnalysts(uniqueAnalysts);
      } catch (error) {
        console.error('Error fetching users list for approved bids:', error);
        setApprovedBidsAllAnalysts([]);
      } finally {
        setIsLoadingApprovedBidsAnalysts(false);
      }
    };

    fetchApprovedBidsUsersList();
  }, []);

  // Fetch user attributes
  useEffect(() => {
    const fetchUserAttributes = async () => {
      if (!userEmail) return;

      setIsLoadingAttributes(true);
      try {
        const response = await bidAnalysisServices.getAttributesWithUsers();
        const attributes: string[] = [];
        const normalizedUserEmail = userEmail.toLowerCase().trim();

        if (response && Array.isArray(response)) {
          response.forEach((attribute: AttributeWithUsers) => {
            if (attribute.is_active && attribute.users && Array.isArray(attribute.users)) {
              const userFound = attribute.users.find(
                (u: { user_details?: { email?: string } }) =>
                  u?.user_details?.email?.toLowerCase().trim() === normalizedUserEmail
              );
              if (userFound) {
                attributes.push(attribute.attribute_name);
              }
            }
          });
        }

        setUserAttributes(attributes);
      } catch (error) {
        console.error('Error fetching user attributes:', error);
        setUserAttributes([]);
      } finally {
        setIsLoadingAttributes(false);
      }
    };

    fetchUserAttributes();
  }, [userEmail]);

  // Handlers
  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    // Reset page when switching tabs
    if (tab === 'review-queue') {
      setCurrentPage(0);
    } else if (tab === 'history') {
      setApprovedBidsCurrentPage(0);
    }
  };

  const handleViewModeToggle = () => {
    setViewMode((prev) => (prev === 'cards' ? 'list' : 'cards'));
  };

  const handleFilterToggle = () => {
    setIsFilterVisible((prev) => !prev);
  };

  const handleFilterChange = (newFilters: FilterState) => {
    setSelectedFilters(newFilters);
  };

  const handleApplyFilters = () => {
    setAppliedFilters(selectedFilters);
    setCurrentPage(0);
  };

  const handlePageChange = (event: any) => {
    const page = Math.floor(event.first / event.rows);
    console.log('Page change event:', { first: event.first, rows: event.rows, calculatedPage: page, currentPage });
    setCurrentPage(page);
  };

  const handleViewNotes = (bid: ManagerDashboardBid) => {
    setSelectedBidForNotes(bid);
    setIsNotesModalVisible(true);
  };

  const handleCloseNotesModal = () => {
    setIsNotesModalVisible(false);
    setSelectedBidForNotes(null);
  };

  const getFirstRecord = () => {
    return currentPage * pageSize;
  };

  const getApprovedBidsFirstRecord = () => {
    return approvedBidsCurrentPage * pageSize;
  };

  // Approved bids handlers
  const handleApprovedBidsFilterChange = (newFilters: FilterState) => {
    setSelectedApprovedBidsFilters(newFilters);
  };

  const handleApprovedBidsApplyFilters = () => {
    setAppliedApprovedBidsFilters(selectedApprovedBidsFilters);
    setApprovedBidsCurrentPage(0);
  };

  const handleApprovedBidsPageChange = (event: any) => {
    const page = Math.floor(event.first / event.rows);
    setApprovedBidsCurrentPage(page);
  };

  return {
    // Data
    bids,
    loading,
    totalRecords,
    pageSize,
    currentPage,

    // View state
    activeTab,
    viewMode,
    isFilterVisible,

    // Filters
    selectedFilters,
    appliedFilters,
    allAnalysts,
    allProductTypes,

    // Approved bids data
    approvedBids,
    approvedBidsLoading,
    approvedBidsTotalRecords,
    approvedBidsCurrentPage,

    // Approved bids filters
    selectedApprovedBidsFilters,
    appliedApprovedBidsFilters,
    approvedBidsAllAnalysts,
    approvedBidsAllCreatedBy,
    isLoadingApprovedBidsAnalysts,
    isLoadingAnalysts,

    // Notes modal
    selectedBidForNotes,
    isNotesModalVisible,

    // User attributes
    userAttributes,
    isLoadingAttributes,

    // Handlers
    handleTabChange,
    handleViewModeToggle,
    handleFilterToggle,
    handleFilterChange,
    handleApplyFilters,
    handlePageChange,
    handleViewNotes,
    handleCloseNotesModal,
    getFirstRecord,

    // Approved bids handlers
    handleApprovedBidsFilterChange,
    handleApprovedBidsApplyFilters,
    handleApprovedBidsPageChange,
    getApprovedBidsFirstRecord,
  };
};

export default useManagerReviewLogic;

